import React from 'react';
import { fmtMoney, fmtPct, fmtDelta, num } from '../../utils/priceMath';

// Financial Performance Summary (F38 / F41).
//
// Cost is split into the five buckets the engine actually produces so that
// outsourced coating is no longer read as "labour/overhead", and the card says
// whether the cost side is the STORED breakdowns or a live recalculation the
// page ran because some lines had none.
//
// Wave 2 (decision 7): when GET /quotes/{id} reports `cost_subtotal` — the
// server's Σ stored breakdown cost × qty, never a recomputation — it is shown
// as "Stored cost subtotal" beside the stored/live label with its coverage
// note (`cost_subtotal_coverage`), and any gap to this card's Σ rows is
// printed rather than hidden. Both keys are optional (older API / new quote).
//
// A Time & Materials quote (the `tm` prop) has no price until its order is priced from actuals: the
// card says "Priced at completion" and shows the cost split with the internal estimate / not-to-
// exceed — never revenue, gross profit or margin (the $0 lines would read as a loss). A fixed-price
// quote (no `tm`) renders exactly as before.

const ROWS = [
  ['material', 'Material (sheet / bar stock)', 'breakdown.material.cost (its unrounded cost_exact when stored) × qty; $0 when customer supplied'],
  ['purchased', 'Purchased components', 'vendor cost of assembly components × qty'],
  ['outsourced', 'Outsourced (coating & vendor steps)', 'operations rows with source "vendor" / no minutes and Coating rows (the engine\'s coating block sums those same rows and is not added again)'],
  ['labour', 'Internal labour (true cost)', 'minutes × punch-weighted wage: laser operator, brake, fab, ops line welding …'],
  ['gasCons', 'Gas & consumables', 'laser gas + nozzle/lens consumables, at cost'],
];

/** "3 of 4 lines carry a stored cost — <note>" from cost_subtotal_coverage; null when nothing usable. */
export function coverageSentence(cov) {
  if (!cov || typeof cov !== 'object') return null;
  const n = num(cov.lines_with_cost, NaN);
  const m = num(cov.lines_total, NaN);
  const bits = [];
  if (Number.isFinite(n) && Number.isFinite(m)) bits.push(`${n} of ${m} lines carry a stored cost`);
  if (cov.note) bits.push(String(cov.note));
  return bits.length ? bits.join(' — ') : null;
}

export default function QuoteFinancialSummaryCard({
  summary, mode, hydratedAt, reason = 'hydrate', costSubtotal = null, costSubtotalCoverage = null, tm = null,
}) {
  if (!summary) return null;
  const b = summary.buckets;
  const showOther = Math.abs(b.other) > 0.005;
  const showRounding = Math.abs(num(b.rounding, 0)) > 0.005;
  const when = hydratedAt && typeof hydratedAt.toLocaleTimeString === 'function' ? ` (${hydratedAt.toLocaleTimeString()})` : '';
  const liveTitle = reason === 'recalculate'
    ? `Recalculate All was applied${when}: every breakdown was re-run at the quote's rates with TODAY's material prices, wages and history. Not saved until Save Changes.`
    : `Some lines had no stored cost breakdown, so the page re-ran the calculator at load${when}. These costs reflect TODAY's material prices, wages and history at the quote's rates and are not saved.`;

  // Decision 7: the server's stored cost subtotal (optional key).
  const stored = num(costSubtotal, NaN);
  const hasStored = Number.isFinite(stored);
  const coverage = coverageSentence(costSubtotalCoverage);
  const storedDelta = hasStored ? stored - num(summary.totalCost, 0) : NaN;
  const storedDiffers = hasStored && Math.abs(storedDelta) > 0.005;
  const storedTitle = 'Server-side Σ stored cost_breakdown.total_cost × qty over the quote lines (purchased components at preferred-vendor cost; customer-supplied material at $0). Never recomputed — a snapshot of what was saved.'
    + (coverage ? ` ${coverage}.` : '');
  const gapReason = mode === 'live'
    ? 'the rows above were recalculated live; the stored figure is the saved snapshot'
    : 'lines without a stored cost are counted differently (typed material price here; vendor cost or nothing there)';

  // The two rows that make Σ rows = the quote's booked cost: an engine cost no bucket names (a real
  // gap, or a classification bug; Quote 349's coating double count showed up here), and the booking of
  // each line's unit cost at 4 dp (jobq-api cost_subtotal / the SO's unit_cost_snapshot).
  const extraRows = (
    <>
      {showOther && (
        <tr>
          <td title="Part of the engine's total_cost that none of the buckets above could name (engine total − Σ buckets)">Not itemised by the engine</td>
          <td className="text-end fw-bold">{fmtMoney(b.other)}</td>
        </tr>
      )}
      {showRounding && (
        <tr>
          <td title="Each line's unit cost is booked at 4 decimals (as the stored cost subtotal and the sales order record it); this is that rounding × qty, not a cost">Rounding (unit cost to 4 dp)</td>
          <td className="text-end fw-bold">{fmtMoney(b.rounding)}</td>
        </tr>
      )}
    </>
  );

  if (tm) {
    const est = num(tm.estimate, NaN);
    const nte = num(tm.nte, NaN);
    return (
      <div className="card shadow-sm border-success h-100" data-testid="tm-financial-summary">
        <div className="card-header bg-success text-white fw-bold d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>Financial Performance Summary</span>
          <span className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge bg-light text-dark" title="Time & Materials: priced on the Sales Order from its own shop time and material after it ships.">T&amp;M</span>
            {mode === 'live' ? (
              <span className="badge bg-warning text-dark" title={liveTitle}>cost: recalculated live (not saved)</span>
            ) : (
              <span className="badge bg-light text-dark" title="Costs come from the breakdowns stored on the quote lines.">cost: as stored</span>
            )}
          </span>
        </div>
        <div className="card-body">
          <h4 className="card-title text-center text-muted mb-1">T&amp;M: priced at completion</h4>
          <div className="text-center text-muted small mb-3">
            No revenue or margin yet: the order is priced on the Sales Order from its own shop time and material after it ships.
          </div>
          <table className="table table-sm mb-2" style={{ fontSize: '0.85rem' }}>
            <tbody>
              {ROWS.map(([key, label, title]) => (
                <tr key={key}>
                  <td title={title}>{label}</td>
                  <td className="text-end fw-bold">{fmtMoney(b[key])}</td>
                </tr>
              ))}
              {extraRows}
              <tr className="table-light">
                <td><strong>Estimated cost</strong> <span className="text-muted small">(Σ rows)</span></td>
                <td className="text-end"><strong>{fmtMoney(summary.totalCost)}</strong></td>
              </tr>
              {hasStored && (
                <tr>
                  <td title={storedTitle}><span className="text-muted">Stored cost subtotal</span></td>
                  <td className="text-end text-muted">{fmtMoney(stored)}</td>
                </tr>
              )}
            </tbody>
          </table>
          {(summary.materialUnknown || []).length > 0 && (
            <div className="alert alert-danger py-1 px-2 small mb-2" data-testid="material-unknown-alert">
              <strong>Material cost unknown</strong> (not $0) on: {summary.materialUnknown.join(', ')}. Its material is left out of the totals, so cost reads low and margin high until the part's material price is fixed (see the line's red “cost unresolved” badge).
            </div>
          )}
          {summary.unresolved.length > 0 && (
            <div className="alert alert-warning py-1 px-2 small mb-2">
              <strong>Cost unresolved</strong> (not $0 — excluded from the total): {summary.unresolved.join(', ')}. The component cost fetch failed; reload or click Recalculate All.
            </div>
          )}
          <div className="d-flex justify-content-between border-top pt-2">
            <div>
              <div className="text-muted small">Internal estimate{tm.estimateIsDefault ? ' (default: the lines at their calculated price)' : ''}</div>
              <div className="fs-5 fw-bold">{Number.isFinite(est) ? fmtMoney(est) : '—'}</div>
            </div>
            {Number.isFinite(nte) && (
              <div className="text-end">
                <div className="text-muted small">Internal not-to-exceed</div>
                <div className="fs-5 fw-bold">{fmtMoney(nte)}</div>
              </div>
            )}
          </div>
          <div className="text-muted small mt-1">Internal budget notes only: not shown to the customer, not a price.</div>
        </div>
        <div className="card-footer text-muted small">
          * Internal labour is true cost (wages), not the sell rate. Machine burden from Shop Rate Config is not included in cost.
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm border-success h-100">
      <div className="card-header bg-success text-white fw-bold d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>Financial Performance Summary</span>
        <span className="d-flex align-items-center gap-2 flex-wrap">
          {mode === 'live' ? (
            <span className="badge bg-warning text-dark" title={liveTitle}>
              cost: recalculated live (not saved)
            </span>
          ) : (
            <span className="badge bg-light text-dark" title="Costs come from the breakdowns stored on the quote lines.">cost: as stored</span>
          )}
          {hasStored && (
            <span className="badge bg-light text-dark" title={storedTitle}>Stored cost subtotal {fmtMoney(stored)}</span>
          )}
        </span>
      </div>
      <div className="card-body">
        <h4 className="card-title text-center text-success mb-3" title="Σ line price × qty; overridden assemblies billed as override × sets">
          {fmtMoney(summary.revenue)} <span className="text-muted fs-6">Revenue</span>
        </h4>
        <table className="table table-sm mb-2" style={{ fontSize: '0.85rem' }}>
          <tbody>
            {ROWS.map(([key, label, title]) => (
              <tr key={key}>
                <td title={title}>{label}</td>
                <td className="text-end fw-bold">{fmtMoney(b[key])}</td>
              </tr>
            ))}
            {extraRows}
            <tr className="table-light">
              <td><strong>Total cost</strong> <span className="text-muted small">(Σ rows)</span></td>
              <td className="text-end"><strong>{fmtMoney(summary.totalCost)}</strong></td>
            </tr>
            {hasStored && (
              <tr>
                <td title={storedTitle}>
                  <span className="text-muted">Stored cost subtotal</span>{' '}
                  <span className="text-muted small">(server, from stored breakdowns{coverage ? `: ${coverage}` : ''})</span>
                  {storedDiffers && (
                    <div className="small text-muted fst-italic" style={{ lineHeight: 1.2 }}>
                      {fmtDelta(storedDelta)} vs Σ rows above — {gapReason}
                    </div>
                  )}
                </td>
                <td className="text-end text-muted">{fmtMoney(stored)}</td>
              </tr>
            )}
          </tbody>
        </table>
        {(summary.materialUnknown || []).length > 0 && (
          <div className="alert alert-danger py-1 px-2 small mb-2" data-testid="material-unknown-alert">
            <strong>Material cost unknown</strong> (not $0) on: {summary.materialUnknown.join(', ')}. Its material is left out of the totals, so cost reads low and margin high until the part's material price is fixed (see the line's red “cost unresolved” badge).
          </div>
        )}
        {summary.unresolved.length > 0 && (
          <div className="alert alert-warning py-1 px-2 small mb-2">
            <strong>Cost unresolved</strong> (not $0 — excluded from the total): {summary.unresolved.join(', ')}. The component cost fetch failed; reload or click Recalculate All.
          </div>
        )}
        {summary.noBreakdown.length > 0 && (
          <div className="text-muted small mb-2">
            No cost breakdown on: {summary.noBreakdown.join(', ')} — only a typed material price (if any) is counted for those lines.
          </div>
        )}
        <div className="d-flex justify-content-between border-top pt-2">
          <div>
            <div className="text-muted small">Gross profit <span title="revenue − total cost">(revenue − cost)</span></div>
            <div className={`fs-5 fw-bold ${summary.grossProfit >= 0 ? 'text-success' : 'text-danger'}`}>{fmtMoney(summary.grossProfit)}</div>
          </div>
          <div className="text-end">
            <div className="text-muted small">Margin <span title="gross profit ÷ revenue">(GP ÷ revenue)</span></div>
            <div className={`fs-5 fw-bold ${summary.margin != null && summary.margin >= 0.2 ? 'text-success' : 'text-danger'}`}>{fmtPct(summary.margin)}</div>
          </div>
        </div>
      </div>
      <div className="card-footer text-muted small">
        * Internal labour is true cost (wages), not the sell rate. Machine burden from Shop Rate Config is not included in cost.
      </div>
    </div>
  );
}
